import { BACKEND_URL } from "@/lib/api";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${BACKEND_URL}/api`;
const POLL_MS = 2000;
const MAX_ATTEMPTS = 30;

export default function AiOrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const navigate = useNavigate();

  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    stopRef.current = false;
    let attempt = 0;
    const poll = async () => {
      if (stopRef.current) return;
      try {
        const { data } = await axios.get(`${API}/ai-orders/status/${sessionId}`);
        if (data?.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch (_) {}
      attempt += 1;
      setAttempts(attempt);
      if (attempt >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
      setTimeout(poll, POLL_MS);
    };
    poll();
    return () => { stopRef.current = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-20">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-soft p-8" data-testid="ai-order-success-card">
        {status === "polling" && (
          <div className="text-center space-y-5">
            <div className="mx-auto inline-grid place-items-center size-14 rounded-2xl bg-[#0F172A] text-white"><Loader2 className="size-6 animate-spin" /></div>
            <h1 className="text-2xl font-semibold text-slate-900">Confirming payment…</h1>
            <p className="text-sm text-slate-600">Stripe is processing your order. This usually takes a few seconds.</p>
            <p className="text-[11px] font-mono text-slate-400">Attempt {attempts + 1} / {MAX_ATTEMPTS}</p>
          </div>
        )}
        {status === "paid" && (
          <div className="text-center space-y-5">
            <div className="mx-auto inline-grid place-items-center size-14 rounded-2xl bg-[#0F172A] text-white"><CheckCircle2 className="size-6" strokeWidth={1.8} /></div>
            <h1 className="text-2xl font-semibold text-slate-900">Order confirmed ✓</h1>
            <p className="text-sm text-slate-600">Our team will reach out within 24 hours to schedule onboarding + integration. You'll receive a receipt by email.</p>
            {orderId && <p className="text-[11px] font-mono text-slate-400">Order ref: {orderId.slice(0, 8)}…</p>}
            <Button onClick={() => navigate("/")} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="ai-order-home">Back to home <ArrowRight className="size-4" /></Button>
          </div>
        )}
        {(status === "timeout" || status === "failed") && (
          <div className="text-center space-y-5">
            <div className="mx-auto inline-grid place-items-center size-14 rounded-2xl bg-[#0F172A] text-white"><AlertCircle className="size-6" /></div>
            <h1 className="text-2xl font-semibold text-slate-900">Still processing</h1>
            <p className="text-sm text-slate-600">If you were charged, please contact <a href="mailto:billing@connex.is" className="text-[#0055FF]">billing@connex.is</a> with your order ref.</p>
            <Button onClick={() => navigate("/ai-systems")} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]">Back to AI Systems</Button>
          </div>
        )}
      </div>
    </div>
  );
}
